import {
  Autocomplete,
  Box,
  BoxStatus,
  Button,
  Flexbox,
  GraphicFilters,
  Label,
  Select,
  SimpleBarChart,
  SlidePnl,
  Stack,
  StackRow,
} from "codekit";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import * as XLSX from "xlsx";
import { getActualDate } from "../../../../utils/globalFunctions";
import { useLazyGetDataDoabfactQuery } from "../../../../libs/redux/slices/Graficsx/Doabfact/doabfactApiSlice";
import { useGetClientGraphicByIdMutation } from "../../../../libs/redux/slices/Reportxx/Repfinac/repfinacApiSlice";

const TABLE_PER_PAGE = 8;
const PANEL_PER_PAGE = 10;

const OP_TYPES = [
  { text: "TODOS", value: "" },
  { text: "IMPORTACION", value: "IMPORTACION" },
  { text: "EXPORTACION", value: "EXPORTACION" },
  { text: "REGISTRO", value: "REGISTRO" },
  { text: "TRANSITO", value: "TRANSITO" },
  { text: "OTROS", value: "OTROS" },
];

const CYCLE_RANGES = [
  { label: "0-15d", min: 0, max: 15, color: "#16A34A" },
  { label: "16-30d", min: 16, max: 30, color: "#2563B0" },
  { label: "31-45d", min: 31, max: 45, color: "#D97706" },
  { label: "46d+", min: 46, max: 9999, color: "#E8472A" },
];

/**
 * @param {string|null} d1 - fecha inicio YYYY-MM-DD
 * @param {string|null} d2 - fecha fin YYYY-MM-DD
 * @returns {number|null} días entre las dos fechas, null si alguna fecha es inválida
 */
const getDaysBetween = (d1, d2) => {
  if (!d1 || !d2) return null;
  const diff = Math.round((new Date(d2 + "T00:00:00") - new Date(d1 + "T00:00:00")) / 86400000);
  return isNaN(diff) ? null : diff;
};

/** @returns {string} fecha de hace 30 días en formato YYYY-MM-DD */
const getThirtyDaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * @param {string} dateStr - YYYY-MM-DD
 * @param {string} groupBy - "mes" | "semana" | "quincena"
 * @returns {string} etiqueta del período
 */
const getPeriodLabel = (dateStr, groupBy) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  if (groupBy === "mes") {
    return d.toLocaleString("es-CO", { month: "short", year: "numeric" });
  }
  if (groupBy === "semana") {
    const s = new Date(d);
    const dow = d.getDay() || 7;
    s.setDate(d.getDate() - dow + 1);
    return `${String(s.getDate()).padStart(2, "0")}/${String(s.getMonth() + 1).padStart(2, "0")}`;
  }
  return `${d.getDate() <= 15 ? "1Q" : "2Q"} ${d.toLocaleString("es-CO", { month: "short" })}`;
};

/**
 * @param {Array} dos - array de DOs del servidor
 * @param {string} groupBy - "mes" | "semana" | "quincena"
 * @returns {Array} datos agrupados por período
 */
const groupDoByPeriod = (dos, groupBy) => {
  const map = {};
  dos.forEach((item) => {
    if (item.REGFECXX) {
      const p = getPeriodLabel(item.REGFECXX, groupBy);
      if (!map[p]) {
        map[p] = { period: p, ref: item.REGFECXX, opened: 0, invoiced: 0, cycleDays: [], income: 0 };
      }
      map[p].opened++;
    }
    if (item.COMHFDEF) {
      const dateStr = item.COMHFDEF.substring(0, 10);
      const pf = getPeriodLabel(dateStr, groupBy);
      if (!map[pf]) {
        map[pf] = { period: pf, ref: dateStr, opened: 0, invoiced: 0, cycleDays: [], income: 0 };
      }
      map[pf].invoiced++;
      if (item.REGFECXX) {
        map[pf].cycleDays.push(getDaysBetween(item.REGFECXX, dateStr));
      }
      map[pf].income += item.INGREXXXX || 0;
    }
  });
  return Object.values(map).sort((a, b) => a.ref.localeCompare(b.ref));
};

/**
 * @param {Array} dos - array de DOs
 * @returns {Object} KPIs calculados
 */
const calcKpis = (dos) => {
  const opened = dos.length;
  const invoiced = dos.filter((d) => d.REGESTXX === "FACTURADO").length;
  const pending = dos.filter((d) => d.REGESTXX !== "FACTURADO").length;
  const pctConv = opened > 0 ? Math.round((invoiced / opened) * 100) : 0;
  const days = dos
    .filter((d) => d.REGESTXX === "FACTURADO" && d.REGFECXX && d.COMHFDEF)
    .map((d) => getDaysBetween(d.REGFECXX, d.COMHFDEF.substring(0, 10)))
    .filter((v) => v !== null);
  return {
    opened,
    invoiced,
    pending,
    pctConv,
    avgDays: days.length > 0 ? Math.round(days.reduce((s, x) => s + x, 0) / days.length) : null,
    maxDays: days.length > 0 ? Math.max(...days) : null,
    minDays: days.length > 0 ? Math.min(...days) : null,
  };
};

/**
 * @param {Array} dos - array de DOs
 * @returns {Array} distribución de ciclos por rango de días
 */
const calcCycleDist = (dos) => {
  const days = dos
    .filter((d) => d.REGESTXX === "FACTURADO" && d.REGFECXX && d.COMHFDEF)
    .map((d) => getDaysBetween(d.REGFECXX, d.COMHFDEF.substring(0, 10)))
    .filter((v) => v !== null);
  const counts = CYCLE_RANGES.map((r) => days.filter((d) => d >= r.min && d <= r.max).length);
  const maxCount = Math.max(...counts, 1);
  return CYCLE_RANGES.map((r, i) => ({
    ...r,
    count: counts[i],
    pct: days.length > 0 ? Math.round((counts[i] / days.length) * 100) : 0,
    barWidth: Math.round((counts[i] / maxCount) * 100),
  }));
};

/**
 * @param {Array} grouped - datos agrupados
 * @returns {Array} formato para SimpleBarChart
 */
const buildChartData = (grouped) =>
  grouped.map((g) => ({
    name: g.period,
    currentDate: "DOs Abiertos",
    previousDate: "DOs Facturados",
    numCurrenDate: g.opened,
    numPreviousDate: g.invoiced,
    url: "",
  }));

/**
 * @param {Object} filters - valores del formulario
 * @returns {Object} parámetros para la API
 */
const buildParams = (filters) => {
  const params = {};
  if (filters.CLIIDXXX) {
    params["MDOSYYYY.CLIIDXXX"] = filters.CLIIDXXX.split(" - ")[0];
  }
  if (filters.REGFECXX_FROM) {
    params["filters[MDOSYYYY.REGFECXX][FROM]"] = filters.REGFECXX_FROM;
  }
  if (filters.REGFECXX_TO) {
    params["filters[MDOSYYYY.REGFECXX][TO]"] = filters.REGFECXX_TO;
  }
  if (filters.DOCTIPXX) {
    params["MDOSYYYY.DOCTIPXX"] = filters.DOCTIPXX;
  }
  return params;
};

/**
 * @param {Array} dos - array de DOs
 * @returns {Array} DOs para el panel: facturados (asc días) luego pendientes
 */
const buildPanelDos = (dos) => [
  ...dos
    .filter((d) => d.REGESTXX === "FACTURADO" && d.REGFECXX && d.COMHFDEF)
    .map((d) => ({ ...d, days: getDaysBetween(d.REGFECXX, d.COMHFDEF.substring(0, 10)) }))
    .sort((a, b) => a.days - b.days),
  ...dos.filter((d) => d.REGESTXX !== "FACTURADO").map((d) => ({ ...d, days: null })),
];

/** @param {number|null} days - días de ciclo */
const getDayColor = (days) => {
  if (days === null) {
    return "#9CA3AF";
  }
  if (days <= 15) {
    return "#16A34A";
  }
  if (days <= 30) {
    return "#2563B0";
  }
  if (days <= 45) {
    return "#D97706";
  }
  return "#E8472A";
};

/** @param {number} pct - porcentaje de conversión */
const getConvStyle = (pct) => {
  if (pct >= 80) {
    return { background: "rgba(22,163,74,.1)", color: "#16A34A" };
  }
  if (pct >= 60) {
    return { background: "rgba(217,119,6,.1)", color: "#D97706" };
  }
  return { background: "rgba(232,71,42,.1)", color: "#E8472A" };
};

const inputDateStyle = {
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  padding: "6px 10px",
  fontFamily: "inherit",
  fontSize: "13px",
  color: "#1a1a2e",
  outline: "none",
  height: "34px",
  width: "100%",
};

const kpiCardBase = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "12px 14px",
  boxShadow: "0 2px 8px rgba(0,0,0,.05)",
  marginBottom: "10px",
};

const cycleCardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "12px 14px",
  boxShadow: "0 2px 8px rgba(0,0,0,.05)",
};

const tableCardStyle = {
  background: "white",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  overflow: "hidden",
  boxShadow: "0 2px 8px rgba(0,0,0,.05)",
  marginTop: "16px",
};

/**
 * Dashboard DOs Abiertos vs Facturados
 * @author Oscar Rosero
 */
const Doabfact = () => {
  const today = getActualDate();
  const [rawData, setRawData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [kpis, setKpis] = useState({
    opened: 0,
    invoiced: 0,
    pending: 0,
    pctConv: 0,
    avgDays: null,
    maxDays: null,
    minDays: null,
  });
  const [cycleDist, setCycleDist] = useState([]);
  const [groupBy, setGroupBy] = useState("mes");
  const [isLoading, setIsLoading] = useState(true);
  const [isPanel, setIsPanel] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [panelPage, setPanelPage] = useState(1);
  const [resultsClient, setResultsClient] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const filtersRef = useRef(null);

  const [getDataDoabfact] = useLazyGetDataDoabfactQuery();
  const [getClientById] = useGetClientGraphicByIdMutation();

  const { control, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      CLIIDXXX: "",
      REGFECXX_FROM: getThirtyDaysAgo(),
      REGFECXX_TO: today,
      DOCTIPXX: "",
    },
  });
  const values = useWatch({ control });

  const handleFetch = useCallback(
    async (filters) => {
      setIsLoading(true);
      await getDataDoabfact(buildParams(filters))
        .unwrap()
        .then((res) => setRawData(res.data || []))
        .catch(() => {})
        .finally(() => setIsLoading(false));
    },
    [getDataDoabfact],
  );

  useEffect(() => {
    filtersRef.current = values;
  }, [values]);

  useEffect(() => {
    handleFetch(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFetch, values]);

  // Polling cada 60 segundos para actualización en tiempo real
  useEffect(() => {
    const id = setInterval(() => setRefreshKey((k) => k + 1), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (refreshKey > 0) {
      handleFetch(filtersRef.current);
    }
  }, [refreshKey, handleFetch]);

  // Recalcular datos locales al cambiar rawData o agrupación
  useEffect(() => {
    setKpis(calcKpis(rawData));
    setCycleDist(calcCycleDist(rawData));
    setGroupedData(groupDoByPeriod(rawData, groupBy));
    setTablePage(1);
  }, [rawData, groupBy]);

  const searchClient = async (CLIIDXXX, cb) => {
    await getClientById({ filters: { CLIIDXXX } })
      .unwrap()
      .then((res) => setResultsClient(res.data))
      .catch(() => {})
      .finally(() => cb());
  };

  const handleReset = () => {
    reset({ CLIIDXXX: "", REGFECXX_FROM: getThirtyDaysAgo(), REGFECXX_TO: today, DOCTIPXX: "" });
    setGroupBy("mes");
  };

  const handleExport = () => {
    const rows = rawData.map((item) => {
      const factDate = item.COMHFDEF ? item.COMHFDEF.substring(0, 10) : null;
      const days = item.REGFECXX && factDate ? getDaysBetween(item.REGFECXX, factDate) : "";
      return {
        DO: item.DOCNROXX || "",
        Factura: item.FACNROXX || "",
        Cliente: item.CLINOMXX || "",
        "Tipo Operación": item.DOCTIPXX || "",
        "Fecha Apertura DO": item.REGFECXX || "",
        "Fecha Facturación": factDate || "",
        "Días Ciclo": days,
        "Valor Ingresos Propios": item.INGREXXXX || "",
        "Estado Facturación": item.REGESTXX === "FACTURADO" ? "FACTURADO" : "PENDIENTE",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DOs");
    XLSX.writeFile(wb, `CIMA_DOs_${today}.xlsx`);
  };
  const chartData = buildChartData(groupedData);
  const maxVal = Math.max(...groupedData.map((g) => Math.max(g.opened, g.invoiced)), 1);
  const topVal = Math.ceil(maxVal * 1.2) || 10;
  const domainXAxis = [0, topVal];
  const ticksXAxis = Array.from({ length: 6 }, (_, i) => Math.round((topVal * i) / 5));

  const tableTotalPages = Math.ceil(groupedData.length / TABLE_PER_PAGE) || 1;
  const tableSlice = groupedData.slice((tablePage - 1) * TABLE_PER_PAGE, tablePage * TABLE_PER_PAGE);

  const panelDos = buildPanelDos(rawData);
  const panelTotalPages = Math.ceil(panelDos.length / PANEL_PER_PAGE) || 1;
  const panelSlice = panelDos.slice((panelPage - 1) * PANEL_PER_PAGE, panelPage * PANEL_PER_PAGE);
  const maxDaysGantt = Math.max(...panelDos.filter((d) => d.days !== null).map((d) => d.days), 1);

  const renderFilters = () => (
    <Box mb={8} borderBottom={1} spacing={2}>
      <Stack gap={3} columnsVariant="form-4">
        <StackRow>
          <Label>Cliente</Label>
          <Controller
            control={control}
            name="CLIIDXXX"
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                placeholder="Buscar por NIT o nombre..."
                textLoading="Buscando..."
                textNoResults="Sin resultados"
                minLengthToSearch={2}
                selected={value}
                searchResults={resultsClient}
                onChangeSearch={(v, cb) => searchClient(v, cb)}
                onItemClick={(ctx) => onChange(ctx.item.label)}
                onResetSelected={() => onChange("")}
              />
            )}
          />
        </StackRow>
        <StackRow>
          <Label>Fecha Inicial</Label>
          <Controller
            control={control}
            name="REGFECXX_FROM"
            render={({ field: { onChange, value } }) => (
              <input type="date" value={value} onChange={(e) => onChange(e.target.value)} style={inputDateStyle} />
            )}
          />
        </StackRow>
        <StackRow>
          <Label>Fecha Final</Label>
          <Controller
            control={control}
            name="REGFECXX_TO"
            render={({ field: { onChange, value } }) => (
              <input type="date" value={value} onChange={(e) => onChange(e.target.value)} style={inputDateStyle} />
            )}
          />
        </StackRow>
        <StackRow>
          <Label>Tipo Operación</Label>
          <Controller
            control={control}
            name="DOCTIPXX"
            render={({ field: { onChange, value } }) => (
              <Select value={value} options={OP_TYPES} onChangeValue={(ctx) => onChange(ctx.option.value)} />
            )}
          />
        </StackRow>
      </Stack>
      <Box mt={4}>
        <Flexbox direction="row" gap={2}>
          <Label>Agrupar por</Label>
          {["semana", "mes", "quincena"].map((g) => (
            <Button
              key={g}
              variant={groupBy === g ? "secondary-bold" : "secondary-light"}
              onClick={() => setGroupBy(g)}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </Button>
          ))}
        </Flexbox>
      </Box>
    </Box>
  );

  const renderKpiCards = () => (
    <>
      <div style={{ ...kpiCardBase, borderTop: "3px solid #2563B0" }}>
        <div
          style={{
            fontSize: "10px",
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: ".05em",
            marginBottom: "6px",
          }}
        >
          DOs Abiertos
        </div>
        <div style={{ fontSize: "24px", fontWeight: 700, color: "#2563B0", lineHeight: 1 }}>{kpis.opened}</div>
        <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "4px" }}>en el período seleccionado</div>
      </div>
      <div style={{ ...kpiCardBase, borderTop: "3px solid #E8472A" }}>
        <div
          style={{
            fontSize: "10px",
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: ".05em",
            marginBottom: "6px",
          }}
        >
          DOs Facturados
        </div>
        <div style={{ fontSize: "24px", fontWeight: 700, color: "#E8472A", lineHeight: 1 }}>{kpis.invoiced}</div>
        <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "4px" }}>en el período seleccionado</div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: "10px",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: "20px",
            marginTop: "6px",
            ...getConvStyle(kpis.pctConv),
          }}
        >
          {kpis.pctConv}% conversión
        </div>
      </div>
      <div style={{ ...kpiCardBase, borderTop: "3px solid #D97706" }}>
        <div
          style={{
            fontSize: "10px",
            fontWeight: 600,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: ".05em",
            marginBottom: "6px",
          }}
        >
          Pendientes de facturar
        </div>
        <div style={{ fontSize: "24px", fontWeight: 700, color: "#D97706", lineHeight: 1 }}>{kpis.pending}</div>
        <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "4px" }}>DOs abiertos sin factura</div>
      </div>
    </>
  );

  const renderCycleCard = () => (
    <div style={cycleCardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <span style={{ fontSize: "11px", fontWeight: 700 }}>Ciclo apertura → factura</span>
        <button
          onClick={() => {
            setIsPanel(true);
            setPanelPage(1);
          }}
          style={{
            background: "none",
            border: "none",
            color: "#2563B0",
            fontSize: "11px",
            fontWeight: 600,
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          Ver detalle →
        </button>
      </div>
      <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
        {[
          { label: "Promedio", val: kpis.avgDays, color: "#7C3AED" },
          { label: "Máximo", val: kpis.maxDays, color: "#E8472A" },
          { label: "Mínimo", val: kpis.minDays, color: "#16A34A" },
        ].map(({ label, val, color }) => (
          <div key={label}>
            <div
              style={{
                fontSize: "9px",
                fontWeight: 600,
                color: "#6B7280",
                textTransform: "uppercase",
                letterSpacing: ".04em",
              }}
            >
              {label}
            </div>
            <div style={{ fontSize: "18px", fontWeight: 700, color }}>{val !== null ? `${val}d` : "—"}</div>
          </div>
        ))}
      </div>
      {cycleDist.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "5px" }}>
          <span style={{ fontSize: "10px", color: "#6B7280", width: "48px", textAlign: "right", flexShrink: 0 }}>
            {r.label}
          </span>
          <div style={{ flex: 1, height: "12px", background: "#F5F6FA", borderRadius: "3px", overflow: "hidden" }}>
            <div
              style={{
                width: `${r.barWidth}%`,
                height: "100%",
                background: r.color,
                borderRadius: "3px",
                transition: "width .4s",
              }}
            />
          </div>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              color: r.color,
              width: "22px",
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {r.count}
          </span>
          <span style={{ fontSize: "10px", color: "#6B7280", width: "28px", textAlign: "right", flexShrink: 0 }}>
            {r.pct}%
          </span>
        </div>
      ))}
    </div>
  );

  const renderTable = () => (
    <div style={tableCardStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <span style={{ fontSize: "12px", fontWeight: 700 }}>Detalle por período</span>
        <span style={{ fontSize: "10px", color: "#6B7280" }}>{groupedData.length} períodos</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#FAFBFC" }}>
            {["Período", "DOs Abiertos", "DOs Facturados", "Pendientes", "% Conversión", "Prom. días ciclo"].map(
              (h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "6px 12px",
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#6B7280",
                    borderBottom: "1px solid #e5e7eb",
                    textTransform: "uppercase",
                    letterSpacing: ".04em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          {tableSlice.map((g) => {
            const pend = Math.max(0, g.opened - g.invoiced);
            const conv = g.opened > 0 ? Math.round((g.invoiced / g.opened) * 100) : 0;
            const promDias =
              g.cycleDays.length > 0 ? Math.round(g.cycleDays.reduce((s, d) => s + d, 0) / g.cycleDays.length) : null;
            return (
              <tr key={g.period} style={{ borderBottom: "1px solid #F5F5F8" }}>
                <td style={{ padding: "7px 12px", fontSize: "12px", fontWeight: 700 }}>{g.period}</td>
                <td style={{ padding: "7px 12px", fontSize: "12px", fontWeight: 700, color: "#2563B0" }}>{g.opened}</td>
                <td style={{ padding: "7px 12px", fontSize: "12px", fontWeight: 700, color: "#E8472A" }}>
                  {g.invoiced}
                </td>
                <td style={{ padding: "7px 12px", fontSize: "12px", fontWeight: 700, color: "#D97706" }}>{pend}</td>
                <td style={{ padding: "7px 12px", fontSize: "12px", fontWeight: 700, ...getConvStyle(conv) }}>
                  {conv}%
                </td>
                <td style={{ padding: "7px 12px", fontSize: "12px", fontWeight: 700, color: getDayColor(promDias) }}>
                  {promDias !== null ? `${promDias}d` : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 14px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <button
            onClick={() => setTablePage((p) => Math.max(1, p - 1))}
            disabled={tablePage === 1}
            style={{
              width: "24px",
              height: "24px",
              border: "1px solid #e5e7eb",
              background: "white",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#6B7280",
            }}
          >
            ‹
          </button>
          <span style={{ fontSize: "11px", color: "#6B7280", padding: "0 6px" }}>
            Pág {tablePage} de {tableTotalPages}
          </span>
          <button
            onClick={() => setTablePage((p) => Math.min(tableTotalPages, p + 1))}
            disabled={tablePage === tableTotalPages}
            style={{
              width: "24px",
              height: "24px",
              border: "1px solid #e5e7eb",
              background: "white",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#6B7280",
            }}
          >
            ›
          </button>
        </div>
        <span style={{ fontSize: "10px", color: "#6B7280" }}>
          {(tablePage - 1) * TABLE_PER_PAGE + 1}–{Math.min(tablePage * TABLE_PER_PAGE, groupedData.length)} de{" "}
          {groupedData.length}
        </span>
      </div>
    </div>
  );

  const renderPanelKpis = () => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "10px", marginBottom: "16px" }}>
      {[
        { label: "Promedio", val: kpis.avgDays, color: "#7C3AED" },
        { label: "Mínimo", val: kpis.minDays, color: "#16A34A" },
        { label: "Máximo", val: kpis.maxDays, color: "#E8472A" },
      ].map(({ label, val, color }) => (
        <div
          key={label}
          style={{
            background: "#F5F6FA",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "10px 12px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "9px",
              fontWeight: 600,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: ".05em",
              marginBottom: "4px",
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: "18px", fontWeight: 700, color }}>{val !== null ? `${val}d` : "—"}</div>
        </div>
      ))}
    </div>
  );

  const renderPanelDist = () => (
    <>
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#1a1a2e",
          marginBottom: "10px",
          paddingBottom: "6px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        Distribución por rango de días
      </div>
      {cycleDist.map((r) => (
        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px" }}>
          <span style={{ fontSize: "10px", color: "#6B7280", width: "55px", textAlign: "right", flexShrink: 0 }}>
            {r.label}
          </span>
          <div style={{ flex: 1, height: "14px", background: "#F5F6FA", borderRadius: "3px", overflow: "hidden" }}>
            <div
              style={{
                width: `${r.barWidth}%`,
                height: "100%",
                background: r.color,
                borderRadius: "3px",
                transition: "width .4s",
              }}
            />
          </div>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              color: r.color,
              width: "22px",
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {r.count}
          </span>
          <span style={{ fontSize: "10px", color: "#6B7280", width: "30px", textAlign: "right", flexShrink: 0 }}>
            {r.pct}%
          </span>
        </div>
      ))}
    </>
  );

  const renderPanelTimeline = () => (
    <>
      <div
        style={{
          fontSize: "11px",
          fontWeight: 700,
          color: "#1a1a2e",
          marginBottom: "10px",
          paddingBottom: "6px",
          borderBottom: "1px solid #e5e7eb",
          marginTop: "16px",
        }}
      >
        Línea de tiempo por DO
      </div>
      {panelSlice.map((item, idx) => (
        <div key={idx} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
          <span
            style={{
              fontSize: "10px",
              color: "#2563B0",
              fontWeight: 700,
              width: "72px",
              flexShrink: 0,
              textAlign: "right",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.DOCNROXX}
          </span>
          <div
            style={{
              flex: 1,
              height: "14px",
              background: "#F5F6FA",
              borderRadius: "3px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {item.days !== null && (
              <div
                style={{
                  width: `${Math.round((item.days / maxDaysGantt) * 100)}%`,
                  height: "100%",
                  background: getDayColor(item.days),
                  borderRadius: "3px",
                  position: "absolute",
                  left: 0,
                }}
              />
            )}
          </div>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              width: "34px",
              textAlign: "right",
              flexShrink: 0,
              color: getDayColor(item.days),
            }}
          >
            {item.days !== null ? (
              `${item.days}d`
            ) : (
              <span style={{ color: "#9CA3AF", fontStyle: "italic" }}>pend.</span>
            )}
          </span>
        </div>
      ))}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: "8px",
          marginTop: "8px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <button
            onClick={() => setPanelPage((p) => Math.max(1, p - 1))}
            disabled={panelPage === 1}
            style={{
              width: "24px",
              height: "24px",
              border: "1px solid #e5e7eb",
              background: "white",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#6B7280",
            }}
          >
            ‹
          </button>
          <span style={{ fontSize: "11px", color: "#6B7280", padding: "0 6px" }}>
            Pág {panelPage} de {panelTotalPages}
          </span>
          <button
            onClick={() => setPanelPage((p) => Math.min(panelTotalPages, p + 1))}
            disabled={panelPage === panelTotalPages}
            style={{
              width: "24px",
              height: "24px",
              border: "1px solid #e5e7eb",
              background: "white",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              color: "#6B7280",
            }}
          >
            ›
          </button>
        </div>
        <span style={{ fontSize: "10px", color: "#6B7280" }}>{panelDos.length} DOs</span>
      </div>
    </>
  );

  const hasData = groupedData.length > 0;

  return (
    <>
      <Box spacing={4}>
        <GraphicFilters
          containerName="DOs Abiertos vs Facturados"
          graphicKeyName="doabfact"
          clearFilters={handleReset}
          onClickVerMas={handleExport}
        >
          <Box>
            {renderFilters()}
            {isLoading ? (
              <BoxStatus variant="loading" text="Cargando información..." />
            ) : !hasData ? (
              <BoxStatus variant="empty" text="Sin datos para el período seleccionado" />
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "16px" }}>
                  <div>
                    <p style={{ fontSize: "12px", fontWeight: 700, marginBottom: "4px" }}>Comparativo por período</p>
                    <p style={{ fontSize: "11px", color: "#6B7280", marginBottom: "10px" }}>
                      Los DOs abiertos en un período pueden facturarse en períodos posteriores — las barras son eventos
                      independientes.
                    </p>
                    <div style={{ display: "flex", gap: "14px", marginBottom: "10px" }}>
                      {[
                        { color: "#2563B0", label: "DOs Abiertos" },
                        { color: "#E8472A", label: "DOs Facturados" },
                      ].map(({ color, label }) => (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            fontSize: "11px",
                            color: "#6B7280",
                          }}
                        >
                          <div style={{ width: "10px", height: "10px", borderRadius: "2px", background: color }} />
                          {label}
                        </div>
                      ))}
                    </div>
                    <SimpleBarChart
                      data={chartData}
                      chartColors={[{ colorBarCurrent: "secondary-600", colorBarPrevious: "error-500" }]}
                      domainXAxis={domainXAxis}
                      ticksXAxis={ticksXAxis}
                      image={false}
                    />
                  </div>
                  <div>
                    {renderKpiCards()}
                    {renderCycleCard()}
                  </div>
                </div>
                {renderTable()}
              </>
            )}
          </Box>
        </GraphicFilters>
      </Box>
      <SlidePnl isOpen={isPanel} onClose={() => setIsPanel(false)} title="Ciclo de vida DO" dotColor="#E8472A">
        {renderPanelKpis()}
        {renderPanelDist()}
        {renderPanelTimeline()}
      </SlidePnl>
    </>
  );
};

export default Doabfact;
